/**
 * LA JOURNÉE — l'écran d'accueil.
 * Il commence par une phrase, jamais par des cartes de statistiques.
 * Puis « À traiter » : trois actions maximum, un tap chacun. Puis la timeline.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CarteLien } from "../components/LienPro";
import {
  ArrowRight, Check, ChevronLeft, ChevronRight, CircleSlash, Clock, CreditCard,
  Plus, Sun, X,
} from "lucide-react";
import { api, messageErreur } from "../api";
import { useApp } from "../App";
import { Barre, Btn, Erreur, Micro, Pastille, Squelette, Vide } from "../components/Bits";
import {
  STATUTS, ajouterJours, dansCombien, duree, euros, isoJour, jourLong, nb,
} from "../lib/format";

/* La synthèse du jour, en français, pas en indicateurs. */
function phraseDuJour(data, aujourdhui) {
  const n = data.entrees.filter((e) => e.status !== "annule").length;
  if (n === 0) {
    return data.ouvert
      ? aujourdhui
        ? "Rien aujourd'hui. Tes créneaux sont libres — c'est le moment de relancer."
        : "Aucun rendez-vous ce jour-là. Tout est encore ouvert."
      : "Tu es fermé ce jour-là. Rien ne t'attend.";
  }
  const heures = Math.round((data.remplissage?.minutes_vendues || 0) / 6) / 10;
  const mots = n === 1 ? "Un rendez-vous" : `${n} rendez-vous`;
  const reste = data.ca_prevu - data.ca_realise;
  if (data.ca_realise > 0 && reste > 0) {
    return `${mots}, ${nb(heures)} h de travail. ${euros(data.ca_realise)} déjà encaissés, ${euros(reste)} à venir.`;
  }
  if (data.ca_realise > 0) {
    return `${mots}, ${nb(heures)} h de travail. Tout est encaissé : ${euros(data.ca_realise)}.`;
  }
  return `${mots}, ${nb(heures)} h de travail, ${euros(data.ca_prevu)} prévus.`;
}

/* Trois actions maximum, dans l'ordre de ce qui coûte le plus cher si on l'oublie. */
function construireActions(data, { encaisser, majStatut, ouvrirRdv, nav }) {
  const out = [];
  for (const e of data.non_traites) {
    out.push({
      id: `clore-${e.id}`,
      ton: "ambre",
      titre: `Tu n'as rien noté pour ${e.client_prenom}`,
      detail: `${e.heure} · ${e.service_nom} · ${euros(e.price)}`,
      note: "Sans ça, tes chiffres et le risque d'absence sont faux.",
      boutons: [
        { l: "Est venu", v: "olive", t: `clore-venue-${e.id}`, f: () => encaisser(e) },
        { l: "Absent", v: "danger", t: `clore-absent-${e.id}`, f: () => majStatut(e.id, "absent", "Absence enregistrée.") },
      ],
    });
  }
  for (const e of data.a_encaisser) {
    out.push({
      id: `encaisser-${e.id}`,
      ton: "accent",
      titre: `Encaisser ${euros(e.price)}`,
      detail: `${e.client_prenom} · ${e.heure} · ${e.service_nom}`,
      boutons: [
        { l: `Encaisser ${euros(e.price)}`, v: "primaire", t: `encaisser-${e.id}`, f: () => encaisser(e) },
      ],
    });
  }
  for (const e of data.a_risque) {
    out.push({
      id: `risque-${e.id}`,
      ton: "brique",
      titre: `${e.client_prenom} risque de ne pas venir`,
      detail: `${e.heure} · ${e.risque?.facteurs?.[0] || "Historique fragile"}`,
      boutons: [
        { l: "Ouvrir la fiche", v: "fantome", t: `risque-ouvrir-${e.id}`, f: () => ouvrirRdv({ rdv: e }) },
      ],
    });
  }
  for (const c of data.creux) {
    out.push({
      id: `creux-${c.label}`,
      ton: "neutre",
      titre: `${duree(c.minutes)} de creux à ${c.label.split(" ")[0]}`,
      detail: "Une relance bien placée le remplit souvent.",
      boutons: [{ l: "Relancer", v: "fantome", t: "creux-relancer", f: () => nav("/relances") }],
    });
  }
  return out.slice(0, 3);
}

const TON_BORD = {
  accent: "var(--accent)",
  ambre: "var(--warning)",
  brique: "var(--danger)",
  neutre: "var(--border-strong)",
};

export default function Journee() {
  const { compte, version, rafraichir, ouvrirRdv } = useApp();
  const nav = useNavigate();
  const [jour, setJour] = useState(isoJour());
  const [data, setData] = useState(null);
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState(null);

  const assistant = compte?.plan?.assistant;

  const charger = useCallback(async () => {
    setCharge(true);
    setErreur(null);
    try {
      const { data } = await api.get("/journee", {
        params: { jour, briefing: assistant ? true : undefined },
      });
      setData(data);
    } catch (e) {
      setErreur(messageErreur(e, "Impossible de charger ta journée."));
    } finally {
      setCharge(false);
    }
  }, [jour, assistant]);

  useEffect(() => {
    charger();
  }, [charger, version]);

  const majStatut = async (id, status, message) => {
    try {
      await api.patch(`/rendez-vous/${id}`, { status });
      toast.success(message);
      rafraichir();
    } catch (e) {
      toast.error(messageErreur(e));
    }
  };

  const encaisser = async (rdv) => {
    try {
      await api.post(`/rendez-vous/${rdv.id}/encaisser`, { payment_method: "carte" });
      toast.success(`${euros(rdv.price)} encaissés.`);
      rafraichir();
    } catch (e) {
      toast.error(messageErreur(e));
    }
  };

  const aujourdhui = jour === isoJour();
  const actions = useMemo(
    () => (data ? construireActions(data, { encaisser, majStatut, ouvrirRdv, nav }) : []),
    [data] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const vedette = data?.maintenant || data?.suivant;
  const enCours = Boolean(data?.maintenant);

  return (
    <div data-testid="page-journee">
      {/* ------------------------------------------------------- barre de date */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Micro>{aujourdhui ? "Aujourd'hui" : "Ta journée"}</Micro>
          <h1
            className="display text-[26px] md:text-[30px] mt-1.5 first-letter:uppercase truncate"
            data-testid="titre-journee"
          >
            {jourLong(jour)}
          </h1>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {!aujourdhui && (
            <Btn variante="nu" taille="sm" onClick={() => setJour(isoJour())} data-testid="btn-aujourdhui">
              Aujourd'hui
            </Btn>
          )}
          <FlecheJour dir={-1} onClick={() => setJour(ajouterJours(jour, -1))} />
          <FlecheJour dir={1} onClick={() => setJour(ajouterJours(jour, 1))} />
        </div>
      </div>

      {charge ? (
        <div className="pt-6">
          <Squelette lignes={4} />
        </div>
      ) : erreur ? (
        <div className="pt-6">
          <Erreur message={erreur} surReessai={charger} />
        </div>
      ) : (
        <>
          {/* ------------------------------------------- la phrase de synthèse */}
          <p
            className="text-[17px] md:text-[19px] leading-[1.5] mt-5 max-w-[46ch] pose"
            data-testid="phrase-journee"
          >
            {phraseDuJour(data, aujourdhui)}
          </p>

          <div className="mt-6">
            <CarteLien compact />
          </div>

          <div className="grid lg:grid-cols-[1fr_300px] gap-7 lg:gap-10 mt-7">
            <div className="min-w-0">
              {/* ------------------------------------------------- à traiter */}
              {actions.length > 0 && (
                <section className="mb-8" data-testid="bloc-a-traiter">
                  <div className="flex items-baseline justify-between mb-3">
                    <Micro>À traiter</Micro>
                    <span className="num text-[12px] text-[var(--text-subtle)]">
                      {actions.length}
                    </span>
                  </div>
                  <div className="space-y-2.5">
                    {actions.map((a) => (
                      <div
                        key={a.id}
                        data-testid={`traiter-${a.id}`}
                        className="p-4 pose"
                        style={{
                          background: "var(--surface)",
                          border: "1px solid var(--border)",
                          borderLeft: `3px solid ${TON_BORD[a.ton]}`,
                          borderRadius: "var(--radius-lg)",
                          boxShadow: "var(--shadow-sm)",
                        }}
                      >
                        <div className="text-[15px] font-semibold">{a.titre}</div>
                        <div className="text-[13px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                          {a.detail}
                        </div>
                        {a.note && (
                          <div className="text-[12px] mt-1.5" style={{ color: "var(--text-subtle)" }}>
                            {a.note}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2 mt-3.5">
                          {a.boutons.map((b) => (
                            <Btn
                              key={b.l}
                              variante={b.v}
                              taille="sm"
                              onClick={b.f}
                              data-testid={b.t}
                            >
                              {b.l}
                            </Btn>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* --------------------------------------- en cours / prochain */}
              {vedette && (
                <section
                  className="p-5 mb-8 pose"
                  data-testid="bloc-maintenant"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-lg)",
                    boxShadow: "var(--shadow-sm)",
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <Micro>{enCours ? "En cours" : "Prochain rendez-vous"}</Micro>
                    {enCours ? (
                      <Pastille ton={STATUTS[vedette.status]?.ton}>
                        {STATUTS[vedette.status]?.label}
                      </Pastille>
                    ) : (
                      aujourdhui &&
                      vedette.dans_minutes >= 0 && (
                        <Pastille ton="accent">{dansCombien(vedette.dans_minutes)}</Pastille>
                      )
                    )}
                  </div>
                  <div className="flex items-baseline gap-3 mt-3">
                    <span className="num text-[28px] leading-none">{vedette.heure}</span>
                    <span className="text-[18px] font-semibold truncate">
                      {vedette.client_prenom} {vedette.client_nom || ""}
                    </span>
                  </div>
                  <div className="text-[13.5px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                    {vedette.service_nom} · {duree(vedette.duration_min)} · {euros(vedette.price)}
                    {enCours ? ` · se termine à ${vedette.fin}` : ""}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Btn
                      variante="primaire"
                      taille="sm"
                      icone={CreditCard}
                      onClick={() => encaisser(vedette)}
                      data-testid="action-encaisser"
                    >
                      Encaisser {euros(vedette.price)}
                    </Btn>
                    <Btn
                      variante="fantome"
                      taille="sm"
                      icone={enCours ? Check : Clock}
                      onClick={() =>
                        enCours
                          ? majStatut(vedette.id, "arrive", "Arrivée notée.")
                          : majStatut(vedette.id, "confirme", "Rendez-vous confirmé.")
                      }
                      data-testid={enCours ? "action-arrive" : "action-confirme"}
                    >
                      {enCours ? "Il est arrivé" : "Confirmé"}
                    </Btn>
                    <Btn
                      variante="danger"
                      taille="sm"
                      icone={CircleSlash}
                      onClick={() => majStatut(vedette.id, "absent", "Absence enregistrée.")}
                      data-testid="action-absent"
                    >
                      Absent
                    </Btn>
                  </div>
                </section>
              )}

              {data.briefing && (
                <div
                  className="pl-4 mb-8"
                  style={{ borderLeft: "2px solid var(--accent)" }}
                  data-testid="briefing"
                >
                  <Micro>Le point du jour</Micro>
                  <p className="text-[15px] mt-2 leading-relaxed">{data.briefing}</p>
                </div>
              )}

              {/* --------------------------------------------------- timeline */}
              <div className="flex items-center justify-between pb-3 border-b border-[var(--border)]">
                <Micro muted>
                  {data.entrees.length === 0
                    ? "Ta journée"
                    : `${data.entrees.length} rendez-vous · ${euros(data.ca_prevu)}`}
                </Micro>
                <button
                  onClick={() => ouvrirRdv({ jour })}
                  data-testid="btn-ajouter-rdv-jour"
                  className="text-[12.5px] font-semibold inline-flex items-center gap-1"
                  style={{ color: "var(--accent)" }}
                >
                  <Plus size={13} strokeWidth={2.2} /> Ajouter
                </button>
              </div>

              {data.entrees.length === 0 ? (
                <Vide
                  icone={Sun}
                  titre={data.ouvert ? "Journée libre" : "Jour de fermeture"}
                  texte={
                    data.ouvert
                      ? "Aucun rendez-vous pour l'instant. Poste ton lien, ou relance ceux qui ne sont pas revenus."
                      : "Tu es fermé ce jour-là selon tes horaires. Tu peux quand même ajouter un rendez-vous exceptionnel."
                  }
                  action={
                    <Btn onClick={() => ouvrirRdv({ jour })} data-testid="btn-vide-ajouter">
                      Ajouter un rendez-vous
                    </Btn>
                  }
                />
              ) : (
                <div className="timeline pt-4">
                  {data.entrees.map((e) => {
                    const passe = ["honore", "absent", "annule", "annule_tardif"].includes(e.status);
                    const actifBloc = data.maintenant?.id === e.id;
                    return (
                      <div key={e.id} className="relative flex gap-3 pb-2.5">
                        <div className="timeline-heure text-right pr-3">
                          <div className="num text-[14.5px] leading-tight">{e.heure}</div>
                          <div className="text-[11px]" style={{ color: "var(--text-subtle)" }}>
                            {e.fin}
                          </div>
                        </div>
                        <span className={`pastille ${actifBloc ? "pastille-active" : ""}`} />
                        <button
                          onClick={() => ouvrirRdv({ rdv: e })}
                          data-testid={`entree-${e.id}`}
                          className={`rdv-bloc relative flex-1 min-w-0 text-left p-3.5 ml-2 ${passe ? "opacity-60" : ""}`}
                          style={{
                            borderLeftColor:
                              e.status === "absent" || e.status === "annule_tardif"
                                ? "var(--danger)"
                                : e.status === "annule"
                                ? "var(--border-strong)"
                                : e.paid
                                ? "var(--success)"
                                : "var(--accent)",
                          }}
                        >
                          {e.annule_par === "client" && (
                            <span
                              className="absolute -top-2 -right-2 inline-flex items-center justify-center rounded-full"
                              style={{ width: 20, height: 20, background: "var(--danger)", color: "#fff" }}
                              title="La cliente a annulé"
                              data-testid={`badge-annule-client-${e.id}`}
                            >
                              <X size={12} strokeWidth={2.6} />
                            </span>
                          )}
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-[15px] font-semibold truncate">
                                {e.client_prenom} {e.client_nom || ""}
                              </div>
                              <div
                                className="text-[13px] mt-0.5 truncate"
                                style={{ color: "var(--text-muted)" }}
                              >
                                {e.service_nom} · {duree(e.duration_min)}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="num text-[15px]">{euros(e.price)}</div>
                              <div className="flex justify-end mt-1.5">
                                {e.paid ? (
                                  <Pastille ton="olive">Payé</Pastille>
                                ) : e.status !== "prevu" ? (
                                  <Pastille ton={STATUTS[e.status]?.ton}>
                                    {STATUTS[e.status]?.label}
                                  </Pastille>
                                ) : e.risque?.niveau === "eleve" ? (
                                  <Pastille ton="brique">Risque</Pastille>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ---------------------------------------------- colonne droite */}
            <aside className="space-y-4">
              <div
                className="p-4"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-lg)",
                }}
              >
                <Micro>Encaissé ce jour</Micro>
                <div className="num text-[30px] leading-none mt-2" data-testid="ca-realise">
                  {euros(data.ca_realise)}
                </div>
                <div className="mt-5">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-[13px]" style={{ color: "var(--text-muted)" }}>
                      Remplissage
                    </span>
                    <span className="num text-[13.5px]">{nb(data.remplissage.taux)} %</span>
                  </div>
                  <Barre pct={data.remplissage.taux} />
                  <p className="text-[12px] mt-2.5" style={{ color: "var(--text-subtle)" }}>
                    {nb(Math.round((data.remplissage.minutes_vendues / 60) * 10) / 10)} h vendues sur{" "}
                    {nb(Math.round((data.remplissage.minutes_ouvrables / 60) * 10) / 10)} h ouvrables.
                    {data.remplissage.taux < 60 && data.ouvert
                      ? " Il te reste de la place."
                      : ""}
                  </p>
                </div>
              </div>

              <button
                onClick={() => nav("/agenda")}
                data-testid="lien-agenda-semaine"
                className="w-full flex items-center justify-between gap-3 px-4 h-12 text-[14px] font-semibold cible-tactile"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                Voir ma semaine
                <ArrowRight size={16} strokeWidth={1.9} style={{ color: "var(--accent)" }} />
              </button>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

function FlecheJour({ dir, onClick }) {
  const I = dir < 0 ? ChevronLeft : ChevronRight;
  return (
    <button
      onClick={onClick}
      data-testid={dir < 0 ? "jour-precedent" : "jour-suivant"}
      aria-label={dir < 0 ? "Jour précédent" : "Jour suivant"}
      className="h-10 w-10 inline-flex items-center justify-center"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        color: "var(--text-muted)",
        transition: "border-color var(--dur) var(--ease), color var(--dur) var(--ease)",
      }}
    >
      <I size={17} strokeWidth={1.8} />
    </button>
  );
}
